import { IS_BLOCK_EDITOR_MARKDOWN_ENABLED } from '@/components/constants/flagsWithUserQueryDep';
import { beginSerializerFallbackWatch } from '@/components/tiptap/shared/serializer-fallback';
import { reportSerializerFallback } from '@/components/tiptap/shared/serializer-fallback-report';
import { resolveNoteEditorType } from '@/container/ScribeTabs/TabPane/NoteTabPane/hooks/resolveNoteEditorType';
import { useCopySettings } from '@/features/copySettings';
import { copySettingsToWire } from '@/features/copySettings/useCopySettings.helpers';
import { type CopySettings } from '@/features/copySettings/useCopySettings.types';
import { isMarkdownNativeEditorEnabled } from '@/features/flags';
import { IS_REFINED_COPY_ENABLED } from '@/features/flags/legacy-flags';
import { copyCanonicalNote } from '@/helpers/copyHelpers/copyCanonical';
import {
  copyFullHtml,
  copyHtmlWithoutLists,
  copyJsonNote,
  copyPlainText,
  getJsonNoteAsText,
} from '@/helpers/copyHelpers/copyHelpers';
import {
  COPY_SUPERSEDED,
  copyPlainTextV2,
  copyWithFullFormatV2,
  copyWithFullFormatWithoutListsV2,
  copyWithSettingsV2,
  isFormattingOff,
  NOTHING_TO_COPY,
  writeHtmlAndMarkdownToClipboard,
  type CopyOutcome,
} from '@/helpers/copyHelpers/copyHelpers-v2';
import {
  isSupersededCopyError,
  reportCopyFailure,
  reportEmptyCopy,
} from '@/helpers/copyHelpers/reportCopyFailure';
import { flattenTableCellMarkupForPlainText } from '@/helpers/htmlToMarkdown';
import { getWordCount } from '@/helpers/wordCount';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useNoteEditorFlags } from '@/hooks/useNoteEditorFlags';
import { documentSelectionCopyAll } from '@/lib/analytics/scribe/document';
import { sessionCopyClicked } from '@/lib/analytics/scribe/session';
import {
  CopyMethod,
  SessionCopiedFormat,
  type CopySettingsSnapshot,
  type SessionCopySource,
} from '@/models/analytics/features/sessions';
import { type StructuredNote } from '@/types';
import { toast } from '@heidi/design-system/toast';
import {
  stripPersistedEmptySectionHeadingLines,
  type CopyFormattingLoss,
} from '@heidi/editor/core';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { CopyType } from '@heidi/schema/ml-scribe';
import { type Editor } from '@tiptap/react';
import { useCallback } from 'react';

export type SessionCopyOutcome = 'success' | 'failure' | 'nothing_to_copy';

/** Content-free metadata from the actual completed clipboard attempt. */
export type SessionCopySuccess = {
  formattingLoss: CopyFormattingLoss;
  copySettings: Readonly<CopySettings>;
};

export type HandoffCopy = (
  onCopyOutcome?: (outcome: SessionCopyOutcome) => void
) => Promise<void> | void;

type UseSessionCopyOptions = {
  editor: Editor | null;
  markdownContent?: string | null;
  clinicalCodesMarkdown?: string | null;
  isJsonNote?: boolean;
  jsonNote?: StructuredNote | null;
  enableTextEditor?: boolean;
  onSuccess?: (result?: SessionCopySuccess) => void;
  errorTitleKey?: TranslationKey;
  errorDescriptionKey?: TranslationKey;
  /** Which affordance initiated the copy. Required on every consumer. */
  copySource: SessionCopySource;
};

type CopyHandlers = {
  onPlainTextCopy: () => Promise<void> | void;
  onCopyHtmlWithoutLists: () => Promise<void> | void;
  onCopyWithFullHtml: () => Promise<void> | void;
  onCopy: () => Promise<void> | void;
  onHandoffCopy: HandoffCopy;
  updateCopyPreference: (copyType: CopyType) => void;
};

type CopyFlowOptions = {
  copySource?: SessionCopySource;
  onCopyOutcome?: (outcome: SessionCopyOutcome) => void;
  persistCopyPreference?: boolean;
  suppressSuccessNotification?: boolean;
};

type CopyExecutionOptions = {
  copySource: SessionCopySource;
  persistCopyPreference: boolean;
};

/**
 * Shared hook for session copy functionality.
 * Consolidates copy logic from more-dropdown-menu and version-controller components.
 */
export const useSessionCopy = ({
  editor,
  markdownContent,
  clinicalCodesMarkdown,
  isJsonNote = false,
  jsonNote,
  enableTextEditor = true,
  onSuccess,
  errorTitleKey = 'common.error',
  errorDescriptionKey = 'shortcuts.failedToCopy',
  copySource,
}: UseSessionCopyOptions): CopyHandlers => {
  const { t } = useTranslations();
  const { mutate: updateUser } = useMutateUpdateUser();
  const { data: copySettings } = useQueryUser(
    (data) => data?.session_settings?.default_copy_type
  );
  // Four-toggle path. `toggleSettings` is the FE-camelCase CopySettings;
  // `isImprovedCopyEnabled` gates whether we read from it (legacy
  // `default_copy_type` enum stays the source of truth when the FF is
  // off — both write paths are preserved across FF flips per the design
  // doc).
  const { settings: toggleSettings, isImprovedCopyEnabled } = useCopySettings();
  // Same resolver `NoteTabPane` uses to pick the editor, so a copy failure is
  // attributed to the editor the user actually copied from rather than to a
  // second, drifting reading of the same flags.
  const editorFlags = useNoteEditorFlags();
  const editorType = resolveNoteEditorType({
    ...editorFlags,
    shouldUseJSONBlockEditor: isJsonNote,
  });

  const updateCopyPreference = useCallback(
    (copyType: CopyType, shouldPersist = true) => {
      if (shouldPersist && copyType !== copySettings) {
        void updateUser({
          session_settings: {
            default_copy_type: copyType,
          },
        });
      }
    },
    [updateUser, copySettings]
  );

  const trackCopyAnalytics = useCallback(
    (
      format: SessionCopiedFormat,
      text: string,
      // Named rather than positional: at the plain/formatted call sites
      // `htmlFlavorWritten` merely restates `format`, so a bare boolean there
      // trains the reader to skim it — and the two toggle sites, where it is
      // the only thing `format` cannot express, are exactly the ones that must
      // not be skimmed.
      {
        htmlFlavorWritten,
        copySettings,
        formattingLoss,
      }: {
        htmlFlavorWritten: boolean;
        copySettings?: CopySettingsSnapshot;
        /** Only from a measured, completed attempt; omitted everywhere else. */
        formattingLoss?: CopyFormattingLoss;
      },
      copySourceOverride = copySource
    ) => {
      const wordCount = getWordCount(text);
      sessionCopyClicked({
        format,
        copyMethod: CopyMethod.CLICK,
        selectedWordCount: wordCount,
        totalWordCount: wordCount,
        copySource: copySourceOverride,
        htmlFlavorWritten,
        ...(copySettings != null && { copySettings }),
        ...(formattingLoss != null && { formattingLoss }),
      });
    },
    [copySource]
  );

  const handleCopyError = useCallback(() => {
    toast({
      tone: 'destructive',
      title: t(errorTitleKey ?? 'common.error'),
      description: t(errorDescriptionKey ?? 'shortcuts.failedToCopy'),
    });
  }, [t, errorTitleKey, errorDescriptionKey]);

  // Single owner of the success/error contract: every exported handler runs
  // its copy work through here, so `onSuccess` fires exactly once per
  // user-facing copy action and failures always surface exactly one error
  // toast. The `perform*` functions below only copy + track and THROW on
  // failure — they never call `onSuccess` or toast themselves.
  const runCopyFlow = useCallback(
    async (
      performCopy: (
        options: CopyExecutionOptions
      ) => Promise<CopyOutcome | typeof COPY_SUPERSEDED | SessionCopySuccess>,
      flowOptions: CopyFlowOptions = {}
    ) => {
      const resolvedCopySource = flowOptions.copySource ?? copySource;
      const resolvedOnCopyOutcome = flowOptions.onCopyOutcome;
      const resolvedPersistCopyPreference = flowOptions.persistCopyPreference ?? true;
      const didSerializerFallBack = beginSerializerFallbackWatch();
      try {
        const result = await performCopy({
          copySource: resolvedCopySource,
          persistCopyPreference: resolvedPersistCopyPreference,
        });
        // A newer copy replaced this one before it was published: no hint,
        // analytics or empty-copy report. Handoff callers hear `success`, as
        // on main, because the newer copy is what lands on the clipboard.
        if (result === COPY_SUPERSEDED) {
          resolvedOnCopyOutcome?.('success');
          return;
        }
        if (result === NOTHING_TO_COPY) {
          resolvedOnCopyOutcome?.('nothing_to_copy');
          reportEmptyCopy({
            surface: 'note',
            editorType,
            hasEditor: editor != null,
            copySource: resolvedCopySource,
          });
          return;
        }
        // A serializer that swallowed a throw shipped the clipboard its own
        // input, and `session_copied` has already counted it as clean.
        if (didSerializerFallBack()) {
          reportSerializerFallback({ surface: 'copy' });
        }
        resolvedOnCopyOutcome?.('success');
        if (!flowOptions.suppressSuccessNotification) {
          onSuccess?.(result);
        }
      } catch (error) {
        // Bail before any outcome reporting: a superseded native copy is a
        // cancellation, not a failure, so it must not reach `onCopyOutcome`.
        if (isSupersededCopyError(error)) return;
        resolvedOnCopyOutcome?.('failure');
        reportCopyFailure(error, {
          surface: 'note',
          editorType,
          hasEditor: editor != null,
          copySource: resolvedCopySource,
        });
        handleCopyError();
      }
    },
    [onSuccess, handleCopyError, editor, copySource, editorType]
  );

  // The `outcome === NOTHING_TO_COPY` checks in the four `perform*` wrappers
  // below are unreachable while the branch requires an `editor`, which is the
  // only thing the v2 helpers refuse. They are kept so that relaxing that gate
  // cannot silently resurrect a `session_copied` for a copy that wrote nothing.
  const performCopyHtmlWithoutLists = useCallback(
    async ({ copySource, persistCopyPreference }: CopyExecutionOptions) => {
      if (IS_REFINED_COPY_ENABLED() && IS_BLOCK_EDITOR_MARKDOWN_ENABLED() && editor) {
        const outcome = await copyWithFullFormatWithoutListsV2(
          editor,
          clinicalCodesMarkdown ?? undefined
        );
        if (outcome === NOTHING_TO_COPY) return outcome;
      } else {
        await copyHtmlWithoutLists({
          markdownContent: markdownContent ?? '',
          editor,
        });
      }

      updateCopyPreference(
        CopyType.COPY_WITH_FORMATTING_WITHOUT_LISTS,
        persistCopyPreference
      );

      const text =
        (editor?.getText() ?? markdownContent ?? '') + (clinicalCodesMarkdown ?? '');
      trackCopyAnalytics(
        SessionCopiedFormat.FORMAT_WITHOUT_LISTS,
        text,
        {
          htmlFlavorWritten: true,
        },
        copySource
      );
    },
    [
      editor,
      clinicalCodesMarkdown,
      markdownContent,
      updateCopyPreference,
      trackCopyAnalytics,
    ]
  );

  const onCopyHtmlWithoutLists = useCallback(
    () => runCopyFlow(performCopyHtmlWithoutLists),
    [runCopyFlow, performCopyHtmlWithoutLists]
  );

  const performPlainTextCopy = useCallback(
    async ({ copySource, persistCopyPreference }: CopyExecutionOptions) => {
      if (IS_REFINED_COPY_ENABLED() && IS_BLOCK_EDITOR_MARKDOWN_ENABLED() && editor) {
        const outcome = await copyPlainTextV2(editor, clinicalCodesMarkdown ?? undefined);
        if (outcome === NOTHING_TO_COPY) return outcome;
      } else {
        await copyPlainText({
          markdownContent: markdownContent ?? '',
          editor,
        });
      }

      updateCopyPreference(CopyType.COPY_WITH_PLAIN_TEXT, persistCopyPreference);

      const text =
        (editor?.getText() ?? markdownContent ?? '') + (clinicalCodesMarkdown ?? '');
      trackCopyAnalytics(
        SessionCopiedFormat.PLAIN_TEXT,
        text,
        {
          htmlFlavorWritten: false,
        },
        copySource
      );
    },
    [
      editor,
      clinicalCodesMarkdown,
      markdownContent,
      updateCopyPreference,
      trackCopyAnalytics,
    ]
  );

  const onPlainTextCopy = useCallback(
    () => runCopyFlow(performPlainTextCopy),
    [runCopyFlow, performPlainTextCopy]
  );

  const performCopyWithFullHtml = useCallback(
    async ({ copySource, persistCopyPreference }: CopyExecutionOptions) => {
      if (IS_REFINED_COPY_ENABLED() && IS_BLOCK_EDITOR_MARKDOWN_ENABLED() && editor) {
        const outcome = await copyWithFullFormatV2(
          editor,
          clinicalCodesMarkdown ?? undefined
        );
        if (outcome === NOTHING_TO_COPY) return outcome;
      } else {
        await copyFullHtml({
          markdownContent: markdownContent ?? '',
          editor,
        });
      }

      updateCopyPreference(
        CopyType.COPY_WITH_FORMATTING_WITH_LISTS,
        persistCopyPreference
      );

      const text =
        (editor?.getText() ?? markdownContent ?? '') + (clinicalCodesMarkdown ?? '');
      trackCopyAnalytics(
        SessionCopiedFormat.FORMAT_WITH_LISTS,
        text,
        {
          htmlFlavorWritten: true,
        },
        copySource
      );
    },
    [
      editor,
      clinicalCodesMarkdown,
      markdownContent,
      updateCopyPreference,
      trackCopyAnalytics,
    ]
  );

  const onCopyWithFullHtml = useCallback(
    () => runCopyFlow(performCopyWithFullHtml),
    [runCopyFlow, performCopyWithFullHtml]
  );

  // Per-toggle copy execution. Runs only when the FF is on (gated at
  // the call site below). Routes to the v2 (Tiptap) helper when the
  // same conditions the legacy v2 path checks are met. When v2
  // prerequisites aren't met (legacy block-editor users), falls back to
  // the closest legacy v1 helper bucketed from the toggles — per-toggle
  // granularity is a v2-only feature, and `default_copy_type` is
  // intentionally NOT updated here (the new `copy_settings` field is
  // the source of truth when this FF is on).
  const performCopyWithToggles = useCallback(
    async ({ copySource }: CopyExecutionOptions) => {
      if (IS_REFINED_COPY_ENABLED() && IS_BLOCK_EDITOR_MARKDOWN_ENABLED() && editor) {
        const outcome = await copyWithSettingsV2(
          editor,
          toggleSettings,
          clinicalCodesMarkdown ?? undefined
        );
        if (outcome === NOTHING_TO_COPY) return outcome;
      } else if (isFormattingOff(toggleSettings)) {
        // Bucket on formatting-off, not all-off: bullets are plain-text `- `
        // markers, so the migrated plain-text preference (bullets on) still
        // belongs in the plain bucket, mirroring `copyWithSettingsV2`.
        await copyPlainText({ markdownContent: markdownContent ?? '', editor });
      } else if (!toggleSettings.bulletPoints) {
        await copyHtmlWithoutLists({
          markdownContent: markdownContent ?? '',
          editor,
        });
      } else {
        await copyFullHtml({ markdownContent: markdownContent ?? '', editor });
      }

      const text =
        (editor?.getText() ?? markdownContent ?? '') + (clinicalCodesMarkdown ?? '');
      trackCopyAnalytics(
        SessionCopiedFormat.WITH_TOGGLES,
        text,
        {
          htmlFlavorWritten: !isFormattingOff(toggleSettings),
          copySettings: copySettingsToWire(toggleSettings),
        },
        copySource
      );
    },
    [editor, markdownContent, clinicalCodesMarkdown, toggleSettings, trackCopyAnalytics]
  );

  const performSessionCopy = useCallback(
    async ({ copySource, persistCopyPreference }: CopyExecutionOptions) => {
      if (isJsonNote && jsonNote) {
        await copyJsonNote({ jsonNote });
        const jsonText = getJsonNoteAsText(jsonNote);
        trackCopyAnalytics(
          SessionCopiedFormat.PLAIN_TEXT,
          jsonText,
          {
            htmlFlavorWritten: false,
          },
          copySource
        );
        documentSelectionCopyAll();
        return;
      }

      // Labs notes: copy stored canonical markdown directly (the legacy
      // `editor` isn't the markdown-native instance). Skipped for JSON notes
      // and the plain-text-only card (enableTextEditor: false), which fall
      // through below.
      if (
        isMarkdownNativeEditorEnabled() &&
        !isJsonNote &&
        markdownContent &&
        enableTextEditor
      ) {
        // markdownContent doesn't embed clinical codes — append them like the
        // v2 copy helpers so the Labs path doesn't drop them.
        const canonicalMarkdown = clinicalCodesMarkdown
          ? markdownContent + clinicalCodesMarkdown
          : markdownContent;
        // Capture the effective settings before the clipboard await, so the loss
        // hint describes this attempt, not preferences changed while it publishes.
        const copySettingsSnapshot = Object.freeze({ ...toggleSettings });
        const formattingLoss = await copyCanonicalNote({
          markdownContent: canonicalMarkdown,
          settings: copySettingsSnapshot,
          // Two whole-note parses: measure only when the result is used below.
          includeFormattingLoss: isImprovedCopyEnabled,
        });
        if (formattingLoss === COPY_SUPERSEDED) return COPY_SUPERSEDED;
        // Only an improved-copy attempt is measured; never forward anything else.
        const measuredLoss = isImprovedCopyEnabled ? formattingLoss : undefined;
        trackCopyAnalytics(
          SessionCopiedFormat.WITH_TOGGLES,
          canonicalMarkdown,
          {
            htmlFlavorWritten: !isFormattingOff(toggleSettings),
            copySettings: copySettingsToWire(toggleSettings),
            ...(measuredLoss != null && { formattingLoss: measuredLoss }),
          },
          copySource
        );
        documentSelectionCopyAll();
        // Only markdown-native + improved copy report loss metadata; other paths
        // keep the void success result.
        return measuredLoss
          ? { formattingLoss: measuredLoss, copySettings: copySettingsSnapshot }
          : undefined;
      }

      if (!markdownContent && !editor) return NOTHING_TO_COPY;

      if (enableTextEditor) {
        if (isImprovedCopyEnabled) {
          // FF on: per-toggle path owns the entire copy execution.
          await performCopyWithToggles({
            copySource,
            persistCopyPreference,
          });
        } else {
          switch (copySettings) {
            case CopyType.COPY_WITH_PLAIN_TEXT:
              await performPlainTextCopy({
                copySource,
                persistCopyPreference,
              });
              break;
            case CopyType.COPY_WITH_FORMATTING_WITH_LISTS:
              await performCopyWithFullHtml({
                copySource,
                persistCopyPreference,
              });
              break;
            case CopyType.COPY_WITH_FORMATTING_WITHOUT_LISTS:
            default:
              await performCopyHtmlWithoutLists({
                copySource,
                persistCopyPreference,
              });
              break;
          }
        }
        documentSelectionCopyAll();
        return;
      }

      // Copies stored markdown, so it never passes through the editor
      // serializer's `isCopy` pass — flatten here instead, or `serializeTableCell`'s
      // storage markup (`<br>`, `\|`) reaches the clipboard verbatim.
      const presentationMarkdown = flattenTableCellMarkupForPlainText(
        stripPersistedEmptySectionHeadingLines(markdownContent || '')
      );
      await writeHtmlAndMarkdownToClipboard({ markdown: presentationMarkdown });
      trackCopyAnalytics(
        SessionCopiedFormat.PLAIN_TEXT,
        presentationMarkdown,
        {
          htmlFlavorWritten: false,
        },
        copySource
      );
      documentSelectionCopyAll();
    },
    [
      isJsonNote,
      jsonNote,
      markdownContent,
      clinicalCodesMarkdown,
      editor,
      enableTextEditor,
      copySettings,
      isImprovedCopyEnabled,
      toggleSettings,
      performCopyWithToggles,
      performPlainTextCopy,
      performCopyHtmlWithoutLists,
      performCopyWithFullHtml,
      trackCopyAnalytics,
    ]
  );

  // Both handlers use the same copy implementation. The Fullscript variant
  // only changes its analytics source and shared-copy side effects.
  const onCopy = useCallback(
    () => runCopyFlow(performSessionCopy),
    [runCopyFlow, performSessionCopy]
  );

  const onHandoffCopy = useCallback(
    (onCopyOutcome?: (outcome: SessionCopyOutcome) => void) =>
      runCopyFlow(performSessionCopy, {
        copySource: 'fullscript_handoff',
        onCopyOutcome,
        persistCopyPreference: false,
        suppressSuccessNotification: true,
      }),
    [runCopyFlow, performSessionCopy]
  );

  return {
    onPlainTextCopy,
    onCopyHtmlWithoutLists,
    onCopyWithFullHtml,
    onCopy,
    onHandoffCopy,
    updateCopyPreference,
  };
};
